/**
 * PROGRAMADOS — o que ainda vai sair.
 *
 * Apontamento 22 da revisão de UX: "não há onde ver, editar ou cancelar envio
 * agendado".
 *
 * O recurso existia inteiro no servidor desde antes — agendar, listar e
 * cancelar — e no cliente da API. O que não existia era a TELA: quem
 * programava um envio na janela de escrita não tinha para onde voltar. É a
 * mesma classe dos defeitos da fase 5 ("a API aceitava os quatro por PATCH;
 * o painel só criava e apagava"): recurso que existe e não é encontrado não
 * existe.
 *
 * NÃO É UMA PASTA DE VERDADE, e isso é o que faz a tela ser necessária em vez
 * de desnecessária. O ajuste pedido foi "criar pasta Programados", e uma
 * pasta IMAP de verdade seria pior: a mensagem não está no Dovecot ainda —
 * ela vive em `cdw.envios_agendados`, como dado, e só se torna mensagem no
 * instante do envio. Uma pasta exigiria gravá-la duas vezes e depois apagar
 * uma, e o Outlook do cliente mostraria uma pasta em que mexer não faz nada.
 * Aqui a entrada da lateral leva a esta tela, que é o que a pessoa queria.
 *
 * EDITAR É CANCELAR E REABRIR, de propósito. Não há "alterar agendamento" na
 * API, e não deveria haver: o que está guardado é a mensagem inteira em JSON,
 * e um PATCH parcial sobre ela daria dois caminhos para montar a mesma
 * mensagem. Cancelar devolve o texto para a janela de escrita, onde ela foi
 * escrita, com todas as validações de lá.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { dataLonga } from "../ui/formato";

/** O estado, dito como consequência e não como palavra do banco. */
function comoMostrar(estado: string): {
  rotulo: string;
  tom: string;
  explica: string;
} {
  switch (estado) {
    case "agendado":
      return {
        rotulo: "Vai sair",
        tom: "bg-acao-clara text-acao-escura",
        explica: "Ainda dá para cancelar.",
      };
    case "enviando":
      return {
        rotulo: "Saindo agora",
        tom: "bg-aviso/20 text-aviso",
        // Está no contrato e vale repetir aqui: entre o SMTP aceitar e o
        // registro gravar há uma janela em que não se sabe se a mensagem
        // saiu. Cancelar nesse ponto poderia cancelar o que já foi.
        explica: "Cancelar deixou de ser possível — a mensagem está na mão do servidor.",
      };
    case "enviado":
      return {
        rotulo: "Enviada",
        tom: "bg-linha text-tinta-media",
        explica: "Está em Enviados.",
      };
    case "cancelado":
      return {
        rotulo: "Cancelada",
        tom: "bg-linha text-tinta-media",
        explica: "Não foi enviada.",
      };
    default:
      return {
        rotulo: "Não saiu",
        tom: "bg-perigo/15 text-perigo",
        explica: "O envio falhou. O motivo está abaixo.",
      };
  }
}

export function Programados({
  aoReabrir,
}: {
  /** Abre a janela de escrita do Layout com o texto que voltou. */
  aoReabrir: (campos: {
    para?: string;
    cc?: string;
    assunto?: string;
    corpo?: string;
  }) => void;
}) {
  const fila = useQueryClient();
  const [erro, setErro] = useState("");
  const [perdidos, setPerdidos] = useState<string[]>([]);

  const lista = useQuery({
    queryKey: ["agendados"],
    queryFn: () => correio.agendados(),
    // Um envio marcado para as 9h tem que sair da lista às 9h sem a pessoa
    // recarregar — senão ela cancela algo que já saiu e vê um erro.
    refetchInterval: 30_000,
  });

  // CANCELAR E REABRIR, numa ação só.
  //
  // É o "editar" do apontamento 22. O servidor devolve a mensagem no corpo
  // da resposta do cancelamento, e ela vai direto para a janela de escrita
  // — assim o texto nunca passa por um estado em que ninguém o tem.
  const cancelar = useMutation({
    mutationFn: (uuid: string) => correio.cancelarAgendado(uuid),
    onSuccess: (devolvido) => {
      setErro("");
      void fila.invalidateQueries({ queryKey: ["agendados"] });
      aoReabrir({
        para: (devolvido?.para ?? []).join(", "),
        cc: (devolvido?.cc ?? []).join(", "),
        assunto: devolvido?.assunto ?? "",
        corpo: devolvido?.corpo ?? "",
      });
      // O anexo não volta, e isso precisa ser DITO. Reabrir a janela sem o
      // anexo e sem avisar faria a pessoa reenviar achando que está tudo
      // lá — e quem recebe não tem como saber que faltou algo.
      setPerdidos(devolvido?.anexos_perdidos ?? []);
    },
    onError: (e) => setErro(explicar(e)),
  });

  const itens = lista.data ?? [];
  const aSair = itens.filter((a) => a.estado === "agendado" || a.estado === "enviando");
  const passados = itens.filter((a) => a.estado !== "agendado" && a.estado !== "enviando");

  return (
    <div className="min-w-0 flex-1 overflow-y-auto rounded-tl-2xl bg-white">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <h1 className="text-xl text-tinta">Programados</h1>
        <p className="mt-1 text-sm text-tinta-fraca">
          Mensagens com hora marcada para sair. Elas ainda não estão em
          Enviados — e também não estão na sua caixa: ficam guardadas aqui até
          a hora.
        </p>

        {lista.isPending && (
          <p className="mt-8 text-center text-sm text-tinta-fraca">carregando…</p>
        )}
        {lista.isError && (
          <p className="mt-6 text-sm text-perigo">{explicar(lista.error)}</p>
        )}
        {erro && <p className="mt-4 text-sm text-perigo">{erro}</p>}

        {perdidos.length > 0 && (
          <p
            role="status"
            className="mt-4 flex items-start gap-2 rounded-lg border border-aviso/40 bg-aviso/10 px-3 py-2 text-xs text-tinta"
          >
            <Icone nome="Paperclip" tamanho={14} className="mt-0.5 flex-shrink-0 text-aviso" />
            <span>
              O texto voltou para a janela de escrita, mas{" "}
              {perdidos.length === 1 ? "o anexo" : "os anexos"}{" "}
              <strong>{perdidos.join(", ")}</strong>{" "}
              {perdidos.length === 1 ? "não voltou" : "não voltaram"}. Anexe de
              novo antes de enviar.
            </span>
          </p>
        )}

        {!lista.isPending && itens.length === 0 && (
          <p className="mt-8 rounded-xl border border-dashed border-linha px-4 py-8 text-center text-sm text-tinta-fraca">
            Nada programado. Ao escrever uma mensagem, o botão ao lado de
            “Enviar” marca hora para ela sair.
          </p>
        )}

        {aSair.length > 0 && (
          <Secao titulo="Ainda vão sair" itens={aSair} aoCancelar={cancelar} />
        )}

        {/* OS JÁ ENVIADOS FICAM NA TELA, por 7 dias (é o que a API devolve).
            Sem eles, a pessoa programa um envio, ele sai, e a tela fica
            vazia — indistinguível de "sumiu". O contrato diz isso na própria
            rota; a tela precisa mostrar. */}
        {passados.length > 0 && (
          <Secao
            titulo="Dos últimos 7 dias"
            itens={passados}
            aoCancelar={cancelar}
          />
        )}
      </div>
    </div>
  );
}

function Secao({
  titulo,
  itens,
  aoCancelar,
}: {
  titulo: string;
  itens: correio.Agendado[];
  aoCancelar: { mutate: (uuid: string) => void; isPending: boolean };
}) {
  return (
    <section className="mt-6">
      <h2 className="text-sm font-medium text-tinta">{titulo}</h2>
      <ul className="mt-2 space-y-2">
        {itens.map((a) => {
          const v = comoMostrar(a.estado ?? "");
          return (
            <li
              key={a.uuid}
              className="rounded-xl border border-linha bg-white p-4"
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className={`rounded-full px-2 py-0.5 text-xs ${v.tom}`}>
                  {v.rotulo}
                </span>
                <strong className="min-w-0 flex-1 truncate text-sm text-tinta">
                  {a.assunto || "(sem assunto)"}
                </strong>
                {/* A HORA MARCADA POR EXTENSO, não "em 3 dias".
                    É a informação que a pessoa veio conferir, e um relativo
                    obriga a fazer a conta de cabeça para saber se vai sair
                    antes ou depois da reunião. */}
                <span className="text-xs text-tinta-fraca">
                  {dataLonga(a.quando)}
                </span>
              </div>

              <p className="mt-1 truncate text-sm text-tinta-media">
                para {(a.para ?? []).join(", ") || "—"}
              </p>
              <p className="mt-0.5 text-xs text-tinta-fraca">{v.explica}</p>

              {/* SAIU DEPOIS DA HORA, e isso é dito. O laço de envio roda por
                  intervalo, então um atraso de minutos é normal; de horas
                  significa que a API ficou fora do ar, e quem recebeu viu a
                  mensagem chegar na hora errada. */}
              {!!a.atraso_minutos && (
                <p className="mt-1 text-xs text-aviso">
                  Saiu {a.atraso_minutos} minuto
                  {a.atraso_minutos === 1 ? "" : "s"} depois da hora marcada.
                </p>
              )}

              {a.erro && (
                <p className="mt-2 border-l-2 border-perigo pl-3 text-xs text-tinta-media">
                  {a.erro}
                </p>
              )}

              {a.estado === "agendado" && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => aoCancelar.mutate(a.uuid!)}
                    disabled={aoCancelar.isPending}
                    className="flex items-center gap-1.5 rounded-full border border-perigo/30 px-3 py-1.5 text-xs text-perigo hover:bg-perigo/10 disabled:opacity-50"
                  >
                    <Icone nome="X" tamanho={13} />
                    Cancelar o envio
                  </button>
                  <p className="mt-1.5 text-[11px] text-tinta-fraca">
                    Cancelar não apaga o texto: ele volta para a janela de
                    escrita, onde você muda e programa de novo. Anexos
                    precisam ser anexados outra vez.
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
